export type StreamKind = "screen" | "camera";
export type TileSurface = {
  id: string;
  surfaceId: string;
  kind: StreamKind;
  name: string;
  picture?: string;
  self?: boolean;
  live: boolean;
};
export type ConnectionFeedback = {
  state: "testing" | "retrying" | "failed";
  attempt: number;
  maxAttempts: number;
};
export type StreamTileView = TileSurface & {
  order: number;
  local: boolean;
  watching: boolean;
  connecting: boolean;
  connectionFailed: boolean;
  streamEnded: boolean;
  focused: boolean;
  visibleVideo: boolean;
  feedback: ConnectionFeedback | null;
};

export function streamTileViews(
  surfaces: TileSurface[],
  watched: Set<string>,
  focused: string | null,
  feedback: Map<string, ConnectionFeedback>,
  hasMedia: (_surfaceId: string) => boolean,
): StreamTileView[] {
  return surfaces.map((surface, order) => {
    const watching = watched.has(surface.surfaceId);
    const hasVideo = hasMedia(surface.surfaceId);
    const connection = surface.self ? null : feedback.get(surface.id) || null;
    const connectionFailed = Boolean(watching && connection?.state === "failed");
    const streamEnded = watching && !surface.live;
    return {
      id: surface.id,
      surfaceId: surface.surfaceId,
      kind: surface.kind,
      name: surface.name,
      picture: surface.picture || "",
      self: Boolean(surface.self),
      live: surface.live,
      order,
      local: Boolean(surface.self),
      watching,
      connecting: Boolean(watching && surface.live && !hasVideo && !connectionFailed),
      connectionFailed,
      streamEnded,
      focused: focused === surface.surfaceId,
      visibleVideo: hasVideo && !streamEnded && !connectionFailed,
      feedback:
        watching && connection && !(hasVideo && !streamEnded && !connectionFailed)
          ? connection
          : null,
    };
  });
}

// The contain-fit letterbox is deliberately not a focus-toggle target.
export function pointIsOnRenderedVideo(
  video: HTMLVideoElement,
  clientX: number,
  clientY: number,
): boolean {
  if (!video.videoWidth || !video.videoHeight) return false;
  const bounds = video.getBoundingClientRect();
  const scale = Math.min(bounds.width / video.videoWidth, bounds.height / video.videoHeight);
  const width = video.videoWidth * scale,
    height = video.videoHeight * scale;
  const left = bounds.left + (bounds.width - width) / 2;
  const top = bounds.top + (bounds.height - height) / 2;
  return clientX >= left && clientX <= left + width && clientY >= top && clientY <= top + height;
}
